// Hub's rentals and sponsorships settings (ADR 0079): what the settings screen
// reads and saves through bre-hub-rentals. Keys are Booking's KV key names.

export const RENTALS_ACCOUNT_MAPPINGS = [
  { key: 'setting:qbo_stripe_clearing_account_id', label: 'Stripe clearing account', kind: 'account' },
  { key: 'setting:qbo_payout_bank_account_id', label: 'Payout bank account', kind: 'account' },
  { key: 'setting:qbo_merchant_fee_expense_account_id', label: 'Merchant fee expense account', kind: 'account' },
  { key: 'setting:qbo_manual_deposit_account_id', label: 'Manual deposit account', kind: 'account' },
  { key: 'setting:qbo_processing_fee_reimbursement_item_id', label: 'Processing fee item', kind: 'item' },
  { key: 'setting:qbo_refund_item_id', label: 'Refund item', kind: 'item' },
  { key: 'setting:qbo_incidental_item_id', label: 'Incidental item', kind: 'item' },
] as const

export const RENTALS_INVOICE_CATEGORIES = ['Venue Rental', 'AV / Production', 'Catering / Hospitality', 'Staffing', 'Marketing', 'Security', 'Cleaning', 'Equipment', 'Services', 'Other'] as const

export const RENTALS_SETTING_KEYS = [
  'setting:stripe_publishable_key',
  'setting:stripe_test_publishable_key',
  'setting:stripe_test_mode',
  'setting:stripe_fee_pass_through',
  ...RENTALS_ACCOUNT_MAPPINGS.map((m) => m.key),
  'setting:vi_connected_account_id',
  'setting:vi_test_connected_account_id',
  'setting:vi_product_code',
  'setting:vi_coi_requirements_text',
  'setting:vi_cancellation_enabled',
  'setting:vi_test_mode',
  'setting:turnstile_site_key',
  'setting:google_calendar_impersonate_email',
] as const
export type RentalsSettingKey = (typeof RENTALS_SETTING_KEYS)[number]

export const RENTALS_SECRET_FIELDS = [
  { key: 'setting:stripe_secret_key', env: 'STRIPE_SECRET_KEY', label: 'Live restricted key', group: 'stripe' },
  { key: 'setting:stripe_webhook_secret', env: 'STRIPE_WEBHOOK_SECRET', label: 'Live webhook secret', group: 'stripe' },
  { key: 'setting:stripe_test_secret_key', env: 'STRIPE_TEST_SECRET_KEY', label: 'Test restricted key', group: 'stripe' },
  { key: 'setting:stripe_test_webhook_secret', env: 'STRIPE_TEST_WEBHOOK_SECRET', label: 'Test webhook secret', group: 'stripe' },
  { key: 'setting:qbo_client_id', env: 'QBO_CLIENT_ID', label: 'Intuit client ID', group: 'qbo' },
  { key: 'setting:qbo_client_secret', env: 'QBO_CLIENT_SECRET', label: 'Intuit client secret', group: 'qbo' },
  { key: 'setting:vi_client_id', env: 'VI_CLIENT_ID', label: 'Live client ID', group: 'vi' },
  { key: 'setting:vi_client_secret', env: 'VI_CLIENT_SECRET', label: 'Live client secret', group: 'vi' },
  { key: 'setting:vi_webhook_signing_key', env: 'VI_WEBHOOK_SIGNING_KEY', label: 'Live webhook signing key', group: 'vi' },
  { key: 'setting:vi_test_client_id', env: 'VI_TEST_CLIENT_ID', label: 'Test client ID', group: 'vi' },
  { key: 'setting:vi_test_client_secret', env: 'VI_TEST_CLIENT_SECRET', label: 'Test client secret', group: 'vi' },
  { key: 'setting:vi_test_webhook_signing_key', env: 'VI_TEST_WEBHOOK_SIGNING_KEY', label: 'Test webhook signing key', group: 'vi' },
  { key: 'setting:turnstile_secret_key', env: 'TURNSTILE_SECRET_KEY', label: 'Turnstile secret key', group: 'other' },
] as const
export type RentalsSecretKey = (typeof RENTALS_SECRET_FIELDS)[number]['key']

export type RentalsSecretStatus = { key: RentalsSecretKey; source: 'worker' | 'stored' | null; hint: string | null; updatedAt: string | null; updatedBy: string | null }

export type RentalsSettingsView = {
  environment: string
  owners: { settings: string; qbo: string; stripe: string; insurance: string }
  canSave: boolean
  canConnectQbo: boolean
  secretEntry: boolean
  stripeAccountId: string | null
  urls: { stripeWebhook: string; viWebhook: string; qboCallback: string }
  values: Partial<Record<RentalsSettingKey, string>>
  categoryMap: Record<string, string>
  secrets: RentalsSecretStatus[]
  qbo: { connected: boolean; realmId: string | null; status: string | null; accessExpiresAt: string | null; refreshUpdatedAt: string | null; providerOn: boolean }
}

export type RentalsQboLists = { accounts: Array<{ id: string; name: string; type: string }>; items: Array<{ id: string; name: string }> }
export type RentalsStripeCheck = { ok: boolean; mode: 'live' | 'test'; accountId: string | null; matches: boolean | null; status: number | null }
